import type {
  ButtonHTMLAttributes,
  ComponentType,
  ReactNode,
  SVGProps,
} from "react";
import { forwardRef } from "react";
import { Link } from "react-router";
import type { To } from "react-router";
import { cn } from "../../lib/utils";

// ---------------------------------------------------------------------------
// Variants
// ---------------------------------------------------------------------------

type IconType = ComponentType<SVGProps<SVGSVGElement>>;

export type ButtonVariant =
  | "primary"
  | "secondary"
  | "outline"
  | "ghost"
  | "danger";
export type ButtonSize = "sm" | "md" | "lg";

const BASE =
  "inline-flex items-center justify-center gap-2 rounded-full font-semibold whitespace-nowrap select-none transition-all duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50";

const VARIANT: Record<ButtonVariant, string> = {
  primary: "bg-primary text-white shadow-card hover:bg-primary-hover",
  secondary:
    "border border-black/15 bg-white text-ink shadow-card hover:border-primary hover:bg-primary hover:text-white",
  outline:
    "border border-primary bg-primary/10 text-primary shadow-card hover:bg-primary hover:text-white",
  ghost: "text-ink-soft hover:bg-black/[0.04] hover:text-ink",
  danger: "bg-error text-white shadow-card hover:bg-error/90",
};

const SIZE: Record<ButtonSize, string> = {
  sm: "h-8 px-3.5 text-xs",
  md: "h-10 px-5 text-[13px]",
  lg: "h-12 px-6 text-sm",
};

const ICON_SIZE: Record<ButtonSize, string> = {
  sm: "size-3.5",
  md: "size-4",
  lg: "size-5",
};

export interface ButtonStyleOptions {
  variant?: ButtonVariant;
  size?: ButtonSize;
  className?: string;
}

/** Class string for anything that should look like a Button (e.g. a <label>). */
// eslint-disable-next-line react-refresh/only-export-components
export function buttonClasses({
  variant = "primary",
  size = "md",
  className,
}: ButtonStyleOptions = {}) {
  return cn(BASE, VARIANT[variant], SIZE[size], className);
}

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------

interface ButtonContentProps {
  size: ButtonSize;
  leadingIcon?: IconType;
  trailingIcon?: IconType;
  loading?: boolean;
  children?: ReactNode;
}

function ButtonContent({
  size,
  leadingIcon: Leading,
  trailingIcon: Trailing,
  loading,
  children,
}: ButtonContentProps) {
  return (
    <>
      {loading ? (
        <span
          aria-hidden="true"
          className={cn(
            "animate-spin rounded-full border-2 border-current border-t-transparent",
            ICON_SIZE[size]
          )}
        />
      ) : (
        Leading && (
          <Leading aria-hidden="true" className={cn("shrink-0", ICON_SIZE[size])} />
        )
      )}
      {children}
      {Trailing && !loading && (
        <Trailing aria-hidden="true" className={cn("shrink-0", ICON_SIZE[size])} />
      )}
    </>
  );
}

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    ButtonStyleOptions {
  leadingIcon?: IconType;
  trailingIcon?: IconType;
  /** Shows a spinner in place of the leading icon and disables the button. */
  loading?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  function Button(
    {
      variant = "primary",
      size = "md",
      className,
      leadingIcon,
      trailingIcon,
      loading = false,
      disabled,
      type = "button",
      children,
      ...props
    },
    ref
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled || loading}
        aria-busy={loading || undefined}
        className={buttonClasses({ variant, size, className })}
        {...props}
      >
        <ButtonContent
          size={size}
          leadingIcon={leadingIcon}
          trailingIcon={trailingIcon}
          loading={loading}
        >
          {children}
        </ButtonContent>
      </button>
    );
  }
);

// ---------------------------------------------------------------------------
// ButtonLink — same look, renders a router <Link>
// ---------------------------------------------------------------------------

export interface ButtonLinkProps extends ButtonStyleOptions {
  to: To;
  leadingIcon?: IconType;
  trailingIcon?: IconType;
  children?: ReactNode;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  replace?: boolean;
  "aria-label"?: string;
}

export function ButtonLink({
  to,
  variant = "primary",
  size = "md",
  className,
  leadingIcon,
  trailingIcon,
  children,
  ...props
}: ButtonLinkProps) {
  return (
    <Link
      to={to}
      className={buttonClasses({ variant, size, className })}
      {...props}
    >
      <ButtonContent
        size={size}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
      >
        {children}
      </ButtonContent>
    </Link>
  );
}
